import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { AppGlobalsModule } from 'src/app/app-globals.module';
import { StorageService } from '../storage/storage.service';

// Taken from firebase-mesaging.js initiated in .NET
declare const isMessagingSupported: boolean | undefined;
declare const requestPermission: ((isDebug?: boolean) => boolean | Promise<boolean>) | undefined;
declare const canShowBroadcastNotificationPermissionPrompt: (() => boolean | Promise<boolean>) | undefined;

@Injectable({
    providedIn: 'root',
})
export class BroadcastNotificationService {
    private readonly _permissionPromptInterval = 10;
    private readonly _platformId = inject(PLATFORM_ID);
    private readonly _storageService = inject(StorageService);
    private _hasDeferredInitialNavigationPrompt = false;

    private get _isBrowser(): boolean {
        return isPlatformBrowser(this._platformId);
    }

    private get _permissionRequester(): ((isDebug?: boolean) => boolean | Promise<boolean>) | undefined {
        return typeof requestPermission === 'function' ? requestPermission : undefined;
    }

    private get _permissionPromptChecker(): (() => boolean | Promise<boolean>) | undefined {
        return typeof canShowBroadcastNotificationPermissionPrompt === 'function' ? canShowBroadcastNotificationPermissionPrompt : undefined;
    }

    public isNotificationSupported(): boolean {
        if (!this._isBrowser || typeof Notification === 'undefined' || typeof isMessagingSupported === 'undefined') {
            return false;
        }

        return isMessagingSupported === true;
    }

    // Check if notification allowed
    public isNotificationAllowed(): boolean {
        return this._isBrowser && typeof Notification !== 'undefined' && Notification.permission === 'granted';
    }

    // Check if notification denied
    public isNotificationDenied(): boolean {
        return this._isBrowser && typeof Notification !== 'undefined' && Notification.permission === 'denied';
    }

    public canRequestNotificationPermission(): boolean {
        return (
            this.isNotificationSupported() &&
            typeof this._permissionRequester === 'function' &&
            !this.isNotificationAllowed() &&
            !this.isNotificationDenied()
        );
    }

    public async recordNavigationAndShouldShowPermissionPrompt(isInitialNavigation: boolean): Promise<boolean> {
        const navigationCount = this._getStoredNavigationCount() + 1;
        this._storageService.store(AppGlobalsModule.NAVIGATIONS_SUM, navigationCount);

        if (this._hasDeferredInitialNavigationPrompt) {
            this._hasDeferredInitialNavigationPrompt = false;
            return this.canShowNotificationPermissionPrompt();
        }

        if (navigationCount % this._permissionPromptInterval !== 0) {
            return false;
        }

        if (isInitialNavigation) {
            this._hasDeferredInitialNavigationPrompt = true;
            return false;
        }

        return this.canShowNotificationPermissionPrompt();
    }

    public async canShowNotificationPermissionPrompt(): Promise<boolean> {
        const permissionPromptChecker = this._permissionPromptChecker;

        if (!permissionPromptChecker || typeof this._permissionRequester !== 'function') {
            return false;
        }

        try {
            return (await permissionPromptChecker()) === true;
        } catch (error) {
            console.warn('Notification permission prompt check failed.', error);
            return false;
        }
    }

    async requestPermission(): Promise<boolean> {
        const permissionRequester = this._permissionRequester;

        if (this.canRequestNotificationPermission() && permissionRequester) {
            try {
                return (await permissionRequester()) !== false;
            } catch (error) {
                console.warn('Notification permission request failed.', error);
                return false;
            }
        }

        if (!this.isNotificationSupported()) {
            console.warn('Notification is not supported in this browser.');
        } else if (!permissionRequester) {
            console.warn('Notification permission requester is not available.');
        }

        return false;
    }

    private _getStoredNavigationCount(): number {
        const storedCount = this._storageService.load(AppGlobalsModule.NAVIGATIONS_SUM);

        return typeof storedCount === 'number' && Number.isFinite(storedCount) ? storedCount : 0;
    }

    // receiveMessage() {
    //     console.log('new message received. ');
    // }
}
